import { useMemo } from 'react';

import { Case, Subset } from '@/api/types/backend-dto-types';

interface BreadcrumbItem {
  href: string;
  label: string;
  active: boolean;
  iconClass?: string;
}

interface MatterLike {
  name: string;
}

interface UseSubsetBreadcrumbsOptions {
  matterId: string;
  subsetId: string;
  matter: MatterLike | undefined;
  subset: Subset | undefined;
  /**
   * The current page information. If null, indicates this is the subset overview page.
   */
  currentPage: {
    label: string;
    path: string;
  } | null;
}

/**
 * Generates breadcrumb items for subset-level pages.
 * When viewing the "All Documents" subset, it omits the Subsets and subset name items
 * to make it appear as if the user is in the Matter context.
 */
export function useSubsetBreadcrumbs({
  matterId,
  subsetId,
  matter,
  subset,
  currentPage,
}: UseSubsetBreadcrumbsOptions): BreadcrumbItem[] {
  return useMemo(() => {
    if (!matter || !subset) {
      return [{ href: '/matters', label: 'Matters', active: false }];
    }

    const isAllDocumentsSubset = subset.isAllDocuments;

    if (isAllDocumentsSubset) {
      // For All Documents subset, skip the Subsets and subset name items
      const items: BreadcrumbItem[] = [
        { href: '/matters', label: 'Matters', active: false },
        { href: `/matters/${matterId}`, label: matter.name, active: !currentPage, iconClass: 'lucide--folder' },
      ];

      if (currentPage) {
        items.push({
          href: `/matters/${matterId}/subsets/${subsetId}/${currentPage.path}`,
          label: currentPage.label,
          active: true,
        });
      }

      return items;
    }

    // Regular subset - include full breadcrumb trail
    const items: BreadcrumbItem[] = [
      { href: '/matters', label: 'Matters', active: false },
      { href: `/matters/${matterId}`, label: matter.name, active: false, iconClass: 'lucide--folder' },
      { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: false },
      {
        href: `/matters/${matterId}/subsets/${subsetId}`,
        label: subset.name,
        active: !currentPage,
        iconClass: 'lucide--group',
      },
    ];

    if (currentPage) {
      items.push({
        href: `/matters/${matterId}/subsets/${subsetId}/${currentPage.path}`,
        label: currentPage.label,
        active: true,
      });
    }

    return items;
  }, [matter, subset, matterId, subsetId, currentPage]);
}
